// File: src/components/admin/analysis-tasks/TaskDescribeGenerate.js
// "Describe what you need" full task generation view (Issue #64)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, CircularProgress, Alert,
    Divider, Chip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AutoAwesome as AutoAwesomeIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const TaskDescribeGenerate = () => {
    const navigate = useNavigate();
    const [description, setDescription] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generatedConfig, setGeneratedConfig] = useState(null);
    const [error, setError] = useState(null);

    const handleGenerate = async () => {
        if (!description.trim()) return;
        setGenerating(true);
        setError(null);
        setGeneratedConfig(null);

        try {
            const result = await adminApi.aiAssistAnalysisTask({
                action: 'generate_full',
                instruction: description,
                currentConfig: null,
                conversationHistory: [],
            });
            if (result.suggestion) {
                setGeneratedConfig(result.suggestion);
            } else {
                setError('AI did not return a valid configuration. Try again with a more specific description.');
            }
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleAccept = () => {
        if (!generatedConfig) return;
        navigate('/analysis-tasks/new/from-template', {
            state: {
                templateConfig: generatedConfig,
                templateName: 'AI Generated',
                templateId: null,
            },
        });
    };

    const ConfigSection = ({ label, value }) => {
        if (!value && value !== 0) return null;
        const display = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
        return (
            <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {display.length > 200 ? display.slice(0, 200) + '...' : display}
                </Typography>
            </Box>
        );
    };

    return (
        <Box data-testid="task-describe-generate">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/analysis-tasks')}
                    sx={{ mr: 2, color: 'text.secondary' }}
                >
                    Back
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Generate Task with AI
                </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Describe what you need</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Describe the analysis task in plain language. The AI will generate a complete configuration including system prompt, tools, audience rules, and notification settings.
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="e.g., I want a weekly email that reviews campaign performance for premium users and suggests keyword optimizations..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={generating}
                    data-testid="task-describe-input"
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                        onClick={handleGenerate}
                        disabled={generating || !description.trim()}
                        data-testid="task-generate-btn"
                    >
                        {generating ? 'Generating...' : 'Generate'}
                    </Button>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {generatedConfig && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Generated Configuration</Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label={`Tier: ${generatedConfig.dataAccessTier}`} size="small" />
                        <Chip label={`Type: ${generatedConfig.notificationType}`} size="small" />
                        <Chip label={`Tools: ${(generatedConfig.enabledTools || []).length}`} size="small" />
                    </Box>

                    <ConfigSection label="Name" value={generatedConfig.name} />
                    <ConfigSection label="Description" value={generatedConfig.description} />
                    <ConfigSection label="AI Model" value={generatedConfig.aiModel} />
                    <ConfigSection label="System Prompt" value={generatedConfig.systemPrompt} />
                    <ConfigSection label="Enabled Tools" value={(generatedConfig.enabledTools || []).join(', ')} />
                    <ConfigSection label="Channels" value={(generatedConfig.channels || []).join(', ')} />

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" onClick={handleAccept}>
                            Accept & Edit
                        </Button>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={handleGenerate}
                            disabled={generating}
                        >
                            Regenerate
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default TaskDescribeGenerate;
